import { useLayoutEffect, useMemo, useState, MouseEvent, ReactElement } from 'react'
import { flatten, unflatten } from 'flat'
import cloneDeep from 'lodash-es/cloneDeep'
import { FormControl, MenuItem, Stack, TextField, Typography } from '@mui/material'
import type { IfcModelMetadata } from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'
import { mergeSelectedIfcElementGuids } from '@editor/components/Controls/IFCUploadWidget/utils/selectionUtils'
import { SelectIconButton } from '@ui/actions'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'

/** Flattened property dict + set of "key.value" for fast lookup (per guid). */
export interface SearchableIfcElement {
  flatDict: Record<string, string | number>
  flatSet: Set<string>
}

export function buildSearchableFromMetadata(
  metadata: IfcModelMetadata | null,
): Record<string, SearchableIfcElement> {
  const data = metadata?.property_data?.data ?? {}
  const out: Record<string, SearchableIfcElement> = {}

  for (const [guid, entry] of Object.entries(data)) {
    if (!entry || typeof entry !== 'object') continue
    const flatDict = flatten(entry) as Record<string, string | number>
    const flatSet = new Set(Object.entries(flatDict).map(([key, value]) => `${key}.${value}`))
    out[guid] = { flatDict, flatSet }
  }

  return out
}

function capitalizeFirstLetter(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function formatPropertyGroupLabel(key: string): string {
  return capitalizeFirstLetter(key.replace(/PSET_Pset_|PSET_/g, ''))
}

export interface IfcPropertiesViewProps {
  guids: string[]
  metadata: IfcModelMetadata | null
  onSelectGuids?: (guids: string[]) => void
}

/**
 * Displays IFC properties shared by the selected elements (single or multi).
 * Supports "select by property": click the highlight icon on a property value
 * to select all elements that have that exact property key-value.
 */
export function IfcPropertiesView({
  guids,
  metadata,
  onSelectGuids,
}: IfcPropertiesViewProps): ReactElement {
  const [propertyGroupKey, setPropertyGroupKey] = useState<string | undefined>()

  const searchableIfcElements = useMemo(() => buildSearchableFromMetadata(metadata), [metadata])

  const intersectingElementProperties = useMemo(() => {
    const flatDictsToIntersect = guids
      .map(guid => searchableIfcElements[guid])
      .filter(Boolean)
      .sort((a, b) => a.flatSet.size - b.flatSet.size)
      .map(se => se.flatDict)

    let intersectingFlatDict!: Record<string, string | number>

    for (const flatDict of flatDictsToIntersect) {
      if (!intersectingFlatDict) {
        intersectingFlatDict = cloneDeep(flatDict)
        continue
      }

      const intersectedFlatDictAsArray = Object.entries(intersectingFlatDict)
      if (intersectedFlatDictAsArray.length < 1) break

      intersectedFlatDictAsArray.forEach(([key, value]) => {
        if (flatDict[key] !== value) delete intersectingFlatDict[key]
      })
    }

    return unflatten(intersectingFlatDict ?? {}) as Record<string, Record<string, string | number>>
  }, [guids, searchableIfcElements])

  useLayoutEffect(() => {
    setPropertyGroupKey(Object.keys(intersectingElementProperties)[0])
  }, [intersectingElementProperties])

  if (!metadata || guids.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Keine IFC-Metadaten oder keine Auswahl.
      </Typography>
    )
  }

  const hasIntersecting = propertyGroupKey && intersectingElementProperties[propertyGroupKey]
  const groupKeys = Object.keys(intersectingElementProperties)

  if (groupKeys.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Keine gemeinsamen Eigenschaften für die ausgewählten Elemente.
      </Typography>
    )
  }

  return (
    <Stack spacing={1}>
      {guids.length > 1 && (
        <Typography sx={{ fontStyle: 'italic', color: 'grey.600' }} textAlign={'center'}>
          Gemeinsame Eigenschaften von <strong>{guids.length}</strong> Elementen
        </Typography>
      )}

      <FormControl size="small" sx={{ width: '100%' }}>
        <TextField
          select
          label="Eigenschaftsgruppe"
          value={propertyGroupKey ?? ''}
          onChange={e => setPropertyGroupKey(e.target.value)}
          size="small"
          sx={{ width: '100%', bgcolor: 'grey.50' }}
        >
          {groupKeys.map(key => (
            <MenuItem value={key} key={key}>
              {formatPropertyGroupLabel(key)}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>

      {hasIntersecting && (
        <Stack spacing={0}>
          {Object.entries(intersectingElementProperties[propertyGroupKey]).map(
            ([property, value], index) => (
              <IfcPropertyRow
                key={property}
                property={property}
                value={value}
                colored={!!(index % 2)}
                searchKey={`${propertyGroupKey}.${property}.${value}`}
                searchableIfcElements={searchableIfcElements}
                selectedGuids={guids}
                onSelectGuids={onSelectGuids}
              />
            ),
          )}
        </Stack>
      )}
    </Stack>
  )
}

interface IfcPropertyRowProps {
  property: string
  value: string | number
  colored: boolean
  searchKey: string
  searchableIfcElements: Record<string, SearchableIfcElement>
  /** Current selection — shift-click extends it instead of replacing it. */
  selectedGuids: string[]
  onSelectGuids?: (guids: string[]) => void
}

function IfcPropertyRow({
  property,
  value,
  colored,
  searchKey,
  searchableIfcElements,
  selectedGuids,
  onSelectGuids,
}: IfcPropertyRowProps): ReactElement {
  const handleSelectByProperty = (event: MouseEvent<HTMLButtonElement>) => {
    if (!onSelectGuids) return
    const matchingGuids = Object.entries(searchableIfcElements)
      .filter(([, se]) => se.flatSet.has(searchKey))
      .map(([guid]) => guid)
    if (matchingGuids.length > 0) {
      onSelectGuids(
        eventMatchesPointerModifier(event, 'multiSelect')
          ? mergeSelectedIfcElementGuids(selectedGuids, matchingGuids)
          : matchingGuids,
      )
    }
  }

  return (
    <Stack
      direction="row"
      pl={1}
      pr={0.5}
      spacing={1}
      bgcolor={colored ? 'grey.100' : 'white'}
      borderRadius={1}
      alignItems="center"
    >
      <Typography
        flexGrow={1}
        flexShrink={0}
        fontWeight="bold"
        color="grey.600"
        variant="body2"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {capitalizeFirstLetter(property)}
      </Typography>

      <Stack direction="row" spacing={0.5} alignItems="center" overflow="hidden">
        <Typography overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {String(value)}
        </Typography>

        {onSelectGuids && (
          <SelectIconButton
            onClick={handleSelectByProperty}
            size="small"
            title="Alle Elemente mit diesem Eigenschaftswert auswählen"
            aria-label="Alle Elemente mit diesem Eigenschaftswert auswählen"
          />
        )}
      </Stack>
    </Stack>
  )
}
